export type QueueViewport = { left: number; top: number; width: number; height: number };
export type QueuePanelGeometry = { x: number; top: number; maxHeight: number; width: number };

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

/**
 * Derive a queue popup rectangle from a trigger and visible viewport. The
 * explicit top/maxHeight pair avoids combining a fixed bottom offset with a
 * second fixed max-height, which can place the heading above the viewport.
 */
export function computeQueuePanelGeometry(
  anchor: { x: number; y: number; width: number; height: number } | null,
  viewport: QueueViewport,
  options: { reservedBottom?: number; width?: number; gap?: number; minHeight?: number; maxHeight?: number } = {},
): QueuePanelGeometry {
  const gap = Math.max(4, options.gap ?? 10);
  const width = Math.min(options.width ?? 360, Math.max(1, viewport.width - 24));
  const leftMin = viewport.left + 12;
  const leftMax = viewport.left + viewport.width - width - 12;
  const x = clamp(
    anchor ? anchor.x + anchor.width / 2 - width / 2 : leftMax,
    leftMin,
    Math.max(leftMin, leftMax),
  );
  const topMin = viewport.top + 12;
  const reservedBottom = clamp(options.reservedBottom ?? 0, 0, Math.max(0, viewport.height - 24));
  const bottomMax = viewport.top + viewport.height - Math.max(12, reservedBottom);
  const available = Math.max(1, bottomMax - topMin);
  const minHeight = Math.min(Math.max(1, options.minHeight ?? 120), available);
  const maxHeight = Math.min(Math.max(minHeight, options.maxHeight ?? 560), available);

  let top = topMin;
  if (anchor) {
    const below = anchor.y + anchor.height + gap;
    const above = anchor.y - gap - maxHeight;
    if (below + maxHeight <= bottomMax) top = below;
    else if (above >= topMin && above + maxHeight <= bottomMax) top = above;
    // A short viewport cannot fit the full preferred panel below or above the
    // trigger. Pin it to the visible top and let only the list scroll.
    else top = topMin;
  }
  return { x: Math.round(x), top: Math.round(top), maxHeight: Math.round(maxHeight), width: Math.round(width) };
}
